/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import getToken from "../../../utils/getToken";
import { toAbsoluteUrl } from "../../../_flyingPaws/helpers";

const API_URL = process.env.REACT_APP_API_URL;

type LogoVariant = "LOGO_1" | "LOGO_2";

// Previews mirror the artwork bundled in the mobile app build. If the app's
// assets/images/splash-logo*.png change, replace these files too.
const LOGO_OPTIONS: {
  value: LogoVariant;
  label: string;
  preview: string;
}[] = [
  { value: "LOGO_1", label: "Logo 1", preview: "/media/app-logos/logo-1.png" },
  { value: "LOGO_2", label: "Logo 2", preview: "/media/app-logos/logo-2.png" },
];

const cardStyle = { backgroundColor: "#000F1E" };

const AppBrandingWrapper: FC = () => {
  const [activeLogo, setActiveLogo] = useState<LogoVariant>("LOGO_1");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const token = getToken();
        const { data } = await axios({
          url: `${API_URL}/app-branding/all`,
          method: "get",
          headers: { Authorization: token },
        });
        const current = data?.data?.activeLogo;
        if (current === "LOGO_1" || current === "LOGO_2") {
          setActiveLogo(current);
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ?? "Failed to load logo settings"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchBranding();
  }, []);

  const save = async () => {
    const loadingToastId = toast.loading("Saving...");
    setSaving(true);
    try {
      const token = getToken();
      const config: AxiosRequestConfig = {
        url: `${API_URL}/app-branding/edit`,
        method: "patch",
        headers: { Authorization: token },
        data: { activeLogo },
      };
      await axios(config);
      toast.dismiss(loadingToastId);
      toast.success("App logo updated");
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      toast.error(error?.response?.data?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white p-10">Loading logo settings…</div>;
  }

  return (
    <>
      <h1 className="text-white fw-bold mb-5">App Logo</h1>

      <div className="card border-0 mb-5" style={cardStyle}>
        <div className="card-header border-0 pt-6">
          <h3 className="card-title text-white fw-bold m-0">
            Active logo in the mobile app
          </h3>
        </div>

        <div className="card-body pt-3">
          <div className="row g-5">
            {LOGO_OPTIONS.map((option) => {
              const selected = activeLogo === option.value;
              return (
                <div className="col-md-6" key={option.value}>
                  <div
                    onClick={() => setActiveLogo(option.value)}
                    className="h-100 d-flex flex-column align-items-center justify-content-center p-6 rounded"
                    style={{
                      cursor: "pointer",
                      // White plate so logos with light artwork stay visible on
                      // the panel's dark theme.
                      backgroundColor: "#FFFFFF",
                      border: selected
                        ? "3px solid #50CD89"
                        : "3px solid #2B3B52",
                    }}
                  >
                    <img
                      src={toAbsoluteUrl(option.preview)}
                      alt={option.label}
                      style={{
                        maxWidth: "100%",
                        maxHeight: 220,
                        objectFit: "contain",
                      }}
                    />
                    <div className="form-check mt-5">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="activeLogo"
                        id={`logo-${option.value}`}
                        checked={selected}
                        onChange={() => setActiveLogo(option.value)}
                      />
                      <label
                        className="form-check-label fw-bold ms-2"
                        style={{ color: "#000F1E" }}
                        htmlFor={`logo-${option.value}`}
                      >
                        {option.label}
                        {selected ? " (selected)" : ""}
                      </label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-muted fs-8 mt-5">
            This changes the logo shown on the app's splash, login, register,
            home and menu screens. Users pick it up the next time they open the
            app — no app store release needed.
          </div>

          <div className="d-flex justify-content-end mt-5">
            <button className="btn btn-success" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export { AppBrandingWrapper };
export default AppBrandingWrapper;
